<template>
  <v-card
    :class="$vuetify.dark ? 'card-dark ' : 'card-light '"
    style="border-radius: 10px"
  >
    <v-card-title>
      <span class="body-1 beez-bold pl-3">Job Schedules</span>
      <v-spacer />
      <v-list-tile-avatar>
        <v-icon>search</v-icon>
      </v-list-tile-avatar>
      <v-list-tile-content>
        <v-list-tile-sub-title>
          <v-text-field
            v-model="search"
            color="secondary"
            label="Search by Job Name, Group Name, Description"
            xs5
            @change="getJobs"
          >
          </v-text-field>
        </v-list-tile-sub-title>
      </v-list-tile-content>
      <v-tooltip bottom>
        <v-btn
          slot="activator"
          flat
          icon
          @click="
            search = ''
            getJobs
          "
        >
          <v-icon>clear</v-icon>
        </v-btn>
        <span>Clear search</span>
      </v-tooltip>
      <v-tooltip bottom>
        <v-btn slot="activator" flat icon @click="getJobs()">
          <v-icon>cached</v-icon>
        </v-btn>
        <span>Refresh</span>
      </v-tooltip>
    </v-card-title>
    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="schedules"
        :loading="jobLoading"
        :pagination.sync="pagination"
        :rows-per-page-items="rowsPerPageItems"
        :total-items="pagination.totalItems"
        item-key="hexId"
      >
        <v-progress-linear slot="progress" color="accent" indeterminate />
        <template slot="items" slot-scope="props">
          <tr>
            <td max-width="5px">
              <v-menu :nudge-width="200" offset-x>
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on">
                    <v-icon>more_vert</v-icon>
                  </v-btn>
                </template>
                <v-list two-line>
                  <v-list-tile
                    avatar
                    :disabled="
                      props.item.jobStatus === 'PAUSED' || !can('JOB_PAUSE')
                    "
                    @click="pauseJob(props.item)"
                  >
                    <v-list-tile-avatar>
                      <v-icon>pause_circle_filled</v-icon>
                    </v-list-tile-avatar>
                    <v-list-tile-content>
                      <v-list-tile-title>
                        Pause Job
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        Pause all future executions
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>
                  <v-list-tile
                    avatar
                    :disabled="
                      props.item.jobStatus !== 'PAUSED' || !can('JOB_RESUME')
                    "
                    @click="resumeJob(props.item)"
                  >
                    <v-list-tile-avatar>
                      <v-icon>play_circle_filled</v-icon>
                    </v-list-tile-avatar>
                    <v-list-tile-content>
                      <v-list-tile-title>
                        Resume Job
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        Resume all future executions
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>
                  <v-list-tile
                    avatar
                    :disabled="!can('JOB_DELETE')"
                    @click="deleteJob(props.item)"
                  >
                    <v-list-tile-avatar>
                      <v-icon>delete</v-icon>
                    </v-list-tile-avatar>
                    <v-list-tile-content>
                      <v-list-tile-title>
                        Delete Job
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        Delete all future executions
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>
                  <v-list-tile
                    avatar
                    :disabled="!can('JOB_DELETE')"
                    @click="forceDeleteJob(props.item)"
                  >
                    <v-list-tile-avatar>
                      <v-icon>delete_forever</v-icon>
                    </v-list-tile-avatar>
                    <v-list-tile-content>
                      <v-list-tile-title>
                        Force Delete Job
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        Delete all future executions ignoring schedules
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </v-list>
              </v-menu>
            </td>
            <td @click="props.expanded = !props.expanded" class="text-xs-left">
              <div class="body-1 font-weight-bold">
                {{ props.item.jobName }}
              </div>
            </td>
            <td @click="props.expanded = !props.expanded" class="text-xs-left">
              <div class="body-1 font-weight-bold">
                {{ props.item.groupName }}
              </div>
            </td>
            <td @click="props.expanded = !props.expanded" class="text-xs-left">
              <div class="body-1">
                {{ props.item.description }}
              </div>
            </td>
            <td
              @click="props.expanded = !props.expanded"
              class="text-xs-left body-1 font-weight-bold text-capitalize"
            >
              <div class="body-1 font-weight-bold">
                {{ format(props.item.jobStatus) }}
              </div>
            </td>
            <td
              @click="props.expanded = !props.expanded"
              class="text-xs-left body-1 font-weight-bold text-capitalize"
            >
              <div class="caption">{{ format(props.item.executionType) }}</div>
            </td>
            <td @click="props.expanded = !props.expanded" class="text-xs-left">
              <div class="body-1 font-weight-bold">
                {{ props.item.serviceName }}
              </div>
              <div class="caption">
                {{ props.item.serviceDescription }}
              </div>
            </td>
            <td @click="props.expanded = !props.expanded" class="text-xs-left">
              <div v-if="props.item.cronExpression">
                Recurring {{ getCronMessage(props.item.cronExpression) }}
              </div>
              <div v-else>
                One time at {{ getLocalTime(props.item.jobScheduleTime) }}
              </div>
            </td>
            <td @click="props.expanded = !props.expanded" class="text-xs-left">
              <div v-if="props.item.lastFiredTime">
                {{ getLocalTime(props.item.lastFiredTime) }}
              </div>
            </td>
            <td @click="props.expanded = !props.expanded" class="text-xs-left">
              <div v-if="props.item.nextFireTime">
                {{ getLocalTime(props.item.nextFireTime) }}
              </div>
            </td>
          </tr>
        </template>
        <template slot="expand" slot-scope="props">
          <job-card :job-schedule="props.item" />
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import FormGenerator from '@/components/common/form-generator/FormGenerator.vue'
import JobCard from '@/components/bot-scheduler/JobCard.vue'
import _ from 'lodash'
import u from '@/utils'
import moment from 'moment'
import axios from 'axios'
import cronstrue from 'cronstrue'

export default {
  name: 'JobSchedule',
  components: { FormGenerator, JobCard },
  data() {
    return {
      headers: [
        {
          text: 'Actions',
          align: 'center',
          value: 'actions',
          width: '250',
          sortable: false
        },
        { text: 'Job Name', value: 'jobName' },
        { text: 'Group Name', value: 'groupName' },
        { text: 'Job Description', value: 'description' },
        { text: 'Job Status', value: 'jobStatus' },
        { text: 'Job Type', value: 'executionType' },
        {
          text: 'Automation name / Description',
          value: 'executableId',
          sortable: false
        },
        { text: 'Schedule Runs', value: 'cronExpression', sortable: false },
        {
          text: 'Last Executed at',
          value: 'lastFiredTime',
          sortable: false
        },
        {
          text: 'Next Scheduled at',
          value: 'nextFireTime',
          sortable: false
        }
      ],
      jobLoading: false,
      item: null,
      pagination: {
        page: 1,
        rowsPerPage: 5,
        sortBy: 'createdAt',
        descending: false,
        totalItems: 0
      },
      current: {
        page: 1,
        rowsPerPage: 5,
        sortBy: 'createdAt',
        descending: false
      },
      rowsPerPageItems: [5, 10, 25],
      search: '',
      showInput: false,
      schedules: []
    }
  },
  async mounted() {
    await this.getJobs()
  },
  watch: {
    pagination: {
      handler() {
        try {
          if (
            this.pagination.page !== this.current.page ||
            this.pagination.rowsPerPage !== this.current.rowsPerPage ||
            this.pagination.sortBy !== this.current.sortBy ||
            this.pagination.descending !== this.current.descending
          ) {
            this.current.page = this.pagination.page
            this.current.rowsPerPage = this.pagination.rowsPerPage
            this.current.sortBy = this.pagination.sortBy
            this.current.descending = this.pagination.descending
            this.getJobs()
          }
          // eslint-disable-next-line no-empty
        } catch (error) {}
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters({
      can: 'can'
    })
  },
  methods: {
    refresh() {
      this.getJobs()
    },
    format(input) {
      return _.capitalize(_.startCase(input))
    },
    getLocalTime(input) {
      return moment(input)
        .utc()
        .local()
        .format('DD MMM YYYY, hh:mm:ss a')
    },
    getCronMessage(input) {
      return cronstrue.toString(input, {
        dayOfWeekStartIndexZero: false
      })
    },
    getUrlParams() {
      let urlParams = ''
      //fill URL Params from current structure

      urlParams += `&page=${this.current.page}`
      urlParams += `&size=${this.current.rowsPerPage}`
      if (this.current.sortBy) {
        urlParams += `&sort=${this.current.sortBy}`
        if (this.current.descending) {
          urlParams += `,desc`
        }
      }
      if (this.search) {
        urlParams += `&search=${this.search}`
      }
      return urlParams
    },
    async getJobs() {
      this.jobLoading = true
      const urlParams = this.getUrlParams()
      axios
        .get(`/api/v1/jobSchedules?projection=withFireTimes${urlParams}`)
        .then(response => {
          if (response.data._embedded) {
            this.pagination.totalItems = response.data.page.totalElements
            this.schedules = response.data._embedded.jobSchedules.map(item => {
              try {
                item.createdByLocal = this.honeyUsers.find(
                  user => user.hexId === item.createdBy
                ).username
                item.changedByLocal = this.honeyUsers.find(
                  user => user.hexId === item.changedBy
                ).username
                item.service = this.getService(item)
                // eslint-disable-next-line no-empty
              } catch (e) {
                item.createdByLocal =
                  'Error in getting user name - Please refresh'
                item.changedByLocal =
                  'Error in getting user name - Please refresh'
              }
              return item
            })
            this.jobLoading = false
          } else {
            this.pagination.totalItems = 0
            this.schedules = []
            this.jobLoading = false
          }
        })
        .catch(e => {
          console.log(e)
          this.$store.dispatch('directNotification', {
            type: 'error',
            message: 'Job fetch failed',
            description:
              'The Job fetch request has failed - ' + e.response
                ? e.response.data.message
                : 'Error unknown! Kindly retry',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          this.jobLoading = false
        })
    },
    async forceDeleteJob(item) {
      this.jobLoading = true
      axios
        .delete(`/api/v1/jobSchedules/${item.hexId}/force`)
        .then(() => {
          this.$store.dispatch('directNotification', {
            type: 'success',
            message: 'Job deleted',
            description: 'The Job has been deleted successfully',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          this.jobLoading = false
          this.getJobs()
        })
        .catch(e => {
          this.$store.dispatch('directNotification', {
            type: 'error',
            message: 'Job delete failed',
            description:
              'The Job delete request has failed - ' + e.response
                ? e.response.data.message
                : 'Error unknown! Kindly retry',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          this.jobLoading = false
        })
    },

    async deleteJob(item) {
      this.jobLoading = true
      axios
        .delete(`/api/v1/jobSchedules/${item.hexId}`)
        .then(() => {
          this.$store.dispatch('directNotification', {
            type: 'success',
            message: 'Job deleted',
            description: 'The Job has been deleted successfully',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          this.jobLoading = false
          this.getJobs()
        })
        .catch(e => {
          this.$store.dispatch('directNotification', {
            type: 'error',
            message: 'Job delete failed',
            description:
              'The Job delete request has failed - ' + e.response
                ? e.response.data.message
                : 'Error unknown! Kindly retry',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          this.jobLoading = false
        })
    },

    async pauseJob(item) {
      this.jobLoading = true
      axios
        .put(`/api/v1/jobSchedules/${item.hexId}/pause`)
        .then(() => {
          this.$store.dispatch('directNotification', {
            type: 'success',
            message: 'Job paused',
            description: 'The Job has been paused successfully',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          this.jobLoading = false
          this.getJobs()
        })
        .catch(e => {
          this.$store.dispatch('directNotification', {
            type: 'error',
            message: 'Job pause failed',
            description:
              'The Job pause request has failed - ' + e.response
                ? e.response.data.message
                : 'Error unknown! Kindly retry',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          this.jobLoading = false
        })
    },

    async resumeJob(item) {
      this.jobLoading = true
      axios
        .put(`/api/v1/jobSchedules/${item.hexId}/resume`)
        .then(() => {
          this.$store.dispatch('directNotification', {
            type: 'success',
            message: 'Job resumed',
            description: 'The Job has been resumed successfully',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          this.jobLoading = false
          this.getJobs()
        })
        .catch(e => {
          this.$store.dispatch('directNotification', {
            type: 'error',
            message: 'Job resume failed',
            description:
              'The Job resume request has failed - ' + e.response
                ? e.response.data.message
                : 'Error unknown! Kindly retry',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          this.jobLoading = false
        })
    },

    async stopJob(item) {
      this.jobLoading = true
      axios
        .put(`/api/v1/jobSchedules/${item.hexId}/stop`)
        .then(() => {
          this.$store.dispatch('directNotification', {
            type: 'success',
            message: 'Job stopped',
            description: 'The Job has been stopped successfully',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          this.jobLoading = false
          this.getJobs()
        })
        .catch(e => {
          this.$store.dispatch('directNotification', {
            type: 'error',
            message: 'Job stop failed',
            description:
              'The Job stop request has failed - ' + e.response
                ? e.response.data.message
                : 'Error unknown! Kindly retry',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          this.jobLoading = false
        })
    },

    async startJob(item) {
      this.jobLoading = true
      axios
        .put(`/api/v1/jobSchedules/${item.hexId}/start`)
        .then(() => {
          this.$store.dispatch('directNotification', {
            type: 'success',
            message: 'Job started',
            description: 'The Job has been started successfully',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          this.jobLoading = false
          this.getJobs()
        })
        .catch(e => {
          this.$store.dispatch('directNotification', {
            type: 'error',
            message: 'Job start failed',
            description:
              'The Job start request has failed - ' + e.response
                ? e.response.data.message
                : 'Error unknown! Kindly retry',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          this.jobLoading = false
        })
    }
  }
}
</script>

<style scoped></style>
